import React, { useLayoutEffect, useRef, useState } from 'react';
import { useDynamicDensity } from './useDynamicDensity';
import { paginateResumeDOM } from './splitContent';
import { PageConstraints } from '../../design-system/constraints/pageConstraints';

/**
 * PageEngine wraps the resume layout container.
 * It measures rendering height and performs up to 3 optimization passes
 * before setting final breaks.
 *
 * Props:
 *   showPageBoundaries {boolean} — When true, injects visible A4 page-break guides
 *     into the DOM after pagination. Used by the editor preview so users can see
 *     exactly where page breaks will occur. These guides are hidden via @media print
 *     so they never appear in the downloaded PDF.
 */
export const PageEngine = ({ children, targetPageCount = 1, onPaginationComplete, showPageBoundaries = false, resumeData }) => {
  const containerRef = useRef(null);
  const { densityMode, stepDownDensity, resetDensity, getDensityVariables } = useDynamicDensity();
  const [calculationPass, setCalculationPass] = useState(0);
  const lastResumeDataRef = useRef(resumeData);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    // Reset pagination checks when resume data changes
    if (resumeData !== lastResumeDataRef.current) {
      lastResumeDataRef.current = resumeData;
      resetDensity();
      setCalculationPass(0);
      return;
    }

    const measureAndOptimize = () => {
      const element = containerRef.current;
      const rect = element.getBoundingClientRect();

      // ── [TRACE BUG2:PageEngine] All four height measurements ─────────────
      const _bcrH     = rect.height;
      const _scrollH  = element.scrollHeight;
      const _offsetH  = element.offsetHeight;
      const _clientH  = element.clientHeight;
      console.log(
        `[TRACE BUG2:PageEngine] pass=${calculationPass}` +
        ` getBCR=${_bcrH.toFixed(1)}` +
        ` scrollH=${_scrollH}` +
        ` offsetH=${_offsetH}` +
        ` clientH=${_clientH}` +
        ` targetPageCount=${targetPageCount}` +
        ` A4_HEIGHT=${PageConstraints.A4_HEIGHT}`
      );
      // ─────────────────────────────────────────────────────────────────────

      // Use the unscaled layout dimensions (DPI/zoom independent)
      const actualHeight = Math.max(element.scrollHeight, element.offsetHeight);
      const targetHeight = PageConstraints.A4_HEIGHT * targetPageCount;

      // Iteration logic (limited to MAX_ITERATIONS)
      if (actualHeight > targetHeight && calculationPass < (PageConstraints.MAX_ITERATIONS - 1)) {
        // Step down density (comfortable -> standard -> compact)
        stepDownDensity(densityMode);
        setCalculationPass(prev => prev + 1);
      } else {
        // Final pass: execute element splitting & page breaking
        const finalPages = paginateResumeDOM(element, PageConstraints.A4_HEIGHT);

        // Inject visible page-break guides for the editor preview.
        // These are NOT rendered by Playwright (hidden by @media print in print.css).
        if (showPageBoundaries && finalPages > 1) {
          // Remove any pre-existing guides and gaps first (idempotent)
          element.querySelectorAll('.page-boundary-guide, .page-boundary-gap').forEach(el => el.remove());

          const GAP_HEIGHT = 16;

          for (let page = 1; page < finalPages; page++) {
            const gapTop = PageConstraints.A4_HEIGHT * page + GAP_HEIGHT * (page - 1);

            // Create the physical page gap element
            const gap = document.createElement('div');
            gap.className = 'page-boundary-gap';
            gap.setAttribute('aria-hidden', 'true');
            gap.style.cssText = [
              `position: absolute`,
              `top: ${gapTop}px`,
              `left: 0`,
              `right: 0`,
              `height: ${GAP_HEIGHT}px`,
              `background-color: var(--color-bg-canvas, #f1f5f9)`, // Match editor canvas background
              `box-shadow: inset 0 5px 5px -5px rgba(0,0,0,0.15), inset 0 -5px 5px -5px rgba(0,0,0,0.15)`,
              `z-index: 10`,
              `pointer-events: none`,
            ].join(';');

            // Page number label (e.g. "Page 2" tag next to the gap)
            const label = document.createElement('div');
            label.className = 'page-boundary-guide';
            label.setAttribute('aria-hidden', 'true');
            label.style.cssText = [
              `position: absolute`,
              `top: ${gapTop + 25}px`,
              `right: 8px`,
              `font-size: 9px`,
              `font-family: sans-serif`,
              `color: #8b5cf6`,
              `background: white`,
              `padding: 1px 6px`,
              `border-radius: 4px`,
              `border: 1px solid #8b5cf6`,
              `z-index: 11`,
              `pointer-events: none`,
              `line-height: 1.4`,
              `box-shadow: 0 1px 3px rgba(0,0,0,0.1)`,
            ].join(';');
            label.textContent = `Page ${page + 1}`;

            element.style.position = 'relative';
            element.appendChild(gap);
            element.appendChild(label);
          }
        }

        if (onPaginationComplete) {
          onPaginationComplete({
            pages: finalPages || 1,
            finalDensity: densityMode
          });
        }
      }
    };

    // Run measurement cycles
    measureAndOptimize();
  }, [calculationPass, densityMode, targetPageCount, onPaginationComplete, stepDownDensity, resetDensity, showPageBoundaries, resumeData]);

  const densityStyles = getDensityVariables();

  return (
    <div 
      ref={containerRef} 
      className={`resume-page-engine-container density-${densityMode}`}
      style={{
        width: `${PageConstraints.A4_WIDTH}px`, // Standard A4 width at 96 DPI
        minHeight: `${PageConstraints.A4_HEIGHT}px`, // Standard A4 height
        position: 'relative',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        ...densityStyles
      }}
    >
      {children}
    </div>
  );
};
